import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Documents } from '../../../commons/class/archivo.inteface';
import { HttpService } from '../../../commons/services/http.service';
import { AuthService } from '../../../commons/services/auth.service';
import { SessionStorageService } from '../../../commons/services/sessionl-storage.service';
import { EncryptionDecryptionService } from '../../../commons/services/EncryptionDecryption.service';

export interface Notificacion {
  idNotificacionICOIJ: number;
  estatus: number;
  idOrgano: number;
  circuito: number;
  organoJurisdiccionalPJF: string;
  neun: number;
  expediente: string;
  tipoAsunto: string;
  sTipoCuaderno?: string | null;
  autoridad: string;
  tipoAutoridad: string;
  folioEnvio: number;
  fechaEnvio: string;
  folioRespuesta: number;
  fechaNotificacion: string | null;
  iTipoNotificacion?: number | null;
  tipoInterconexion: TipoInterconexion;
  nombreArchivo?: string;
  catTipoAsuntoId: number | null;
  contemplaLeyAmparo: boolean;
  fechaAcuerdo: string;
  fechaPublica: string;
  sintesis: string;
  autoridaRealizaNot: string;
  TipoNotificacion?: string | null;
  notElecId: number | null;
  fechaRecepcion: string;
  esLaboral: boolean;
  nombreAutoridad: string;
  caracterAutoridad: string;
}

export enum TipoInterconexion {
  Interconexion = 1,
  Psl = 2
}

export interface TotalesNotificaciones {
  oijId: number;
  totalNotificadas: number;
  totalNoNotificadas: number;
}


export interface ActualizarEstatusNotificacionesPslCommand {
  id: number;
  tipoInterconexion: TipoInterconexion;
  nombreArchivo?: string;
  estatus: number;
  idOrgano: number;
  idOij: number;
  catTipoOrganismoIdOij: number;
  tipoCuaderno?: string | null;
  expediente: string;
  neun: number;
  catTipoAsuntoId: number | null;
  idOIJrealizaNot: number | null;
  esLaboral: boolean;
}

export interface ActualizarEstatusNotificacionesPslListCommand {
  items: ActualizarEstatusNotificacionesPslCommand[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificacionesService {
  private readonly currentUserStorageKey = 'currentUser';
  private readonly baseUrl = 'oaj.icoij.gateway/icoij/Notificaciones';
  private totalesSubject = new BehaviorSubject<TotalesNotificaciones | null>(null);
  totales$ = this.totalesSubject.asObservable();
  private sessionStorageService: SessionStorageService = inject(SessionStorageService);
  private httpService: HttpService = inject(HttpService);
  private authService: AuthService = inject(AuthService);
  private httpClient: HttpClient = inject(HttpClient);
  protected serviceED: EncryptionDecryptionService = inject(EncryptionDecryptionService);

  /**
   * Busca notificaciones por fecha de envío, se construye la ruta para la consulta utilizando los parámetros de fecha de inicio, fecha de fin, estatus y tipo de filtro, se hace una petición GET al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con un arreglo de notificaciones
   * @param fechaInicio 
   * @param fechaFin 
   * @param iEstatus 
   * @param tipoFiltro 
   * @returns 
   */
  buscarPorFechaEnvio(
    fechaInicio: string,
    fechaFin: string,
    iEstatus: number | null,
    tipoFiltro: number | null
  ): Observable<Notificacion[]> {
    let params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);

    const organoId = this.authService.getIdOrgan();
    if (organoId !== null && organoId !== undefined) {
      params = params.set('oijId', organoId.toString());
    }

    if (iEstatus !== null && iEstatus !== undefined) {
      params = params.set('estatusId', iEstatus.toString());
    }
    if (tipoFiltro !== null && tipoFiltro !== undefined) {
      params = params.set('tipoFiltro', tipoFiltro.toString());
    }

    return this.httpService.getRequest(
      `${this.baseUrl}/BuscarPorFechaEnvio${this.buildQueryString(params)}`
    ) as Observable<Notificacion[]>;
  }

  /**
   * Actualiza el estatus de una o más notificaciones, se construye la ruta para la actualización utilizando los parámetros de IDs de notificación y el nuevo estatus, se hace una petición POST al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con información sobre la actualización
   * @param idsNotificacion 
   * @param estatus 
   * @returns 
   */
  actualizarEstatus(
    idsNotificacion: number[],
    estatus: number
  ): Observable<{ estatusAplicado: number; totalIdsEnviados: number; filasAfectadas: number }> {
    const params = new HttpParams().set('estatus', estatus.toString());

    return this.httpService.postRequest(
      `${this.baseUrl}/ActualizarEstatus${this.buildQueryString(params)}`,
      idsNotificacion
    ) as Observable<{
      estatusAplicado: number;
      totalIdsEnviados: number;
      filasAfectadas: number;
    }>;
  }

  /**
   * Actualiza el estatus de notificaciones para un grupo específico, se construye la ruta para la actualización utilizando los parámetros de los comandos, se hace una petición POST al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con información sobre la actualización
   * @param items 
   * @returns 
   */
  actualizarEstatusPsl(items: ActualizarEstatusNotificacionesPslCommand[]):
    Observable<{ estatusAplicado: number; totalIdsEnviados: number; filasAfectadas: number }> {

    return this.httpService.postRequest(
      `${this.baseUrl}/ActualizarEstatusPsl`,
      { items }
    ) as Observable<{
      estatusAplicado: number;
      totalIdsEnviados: number;
      filasAfectadas: number;
    }>;
  }


  /**
   * Obtiene los totales de notificaciones para un organo específico, se construye la ruta para la consulta utilizando el ID del organo, se hace una petición GET al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con la información de los totales
   * @param oijId 
   * @returns 
   */
  obtenerTotales(oijId: number): Observable<TotalesNotificaciones> {
    const params = new HttpParams().set('oijId', oijId.toString());
    return this.httpService
      .getRequest(`${this.baseUrl}/Totales${this.buildQueryString(params)}`)
      .pipe(
        map((resp: any) => {
          const data = resp?.succcess ? resp.data : resp;
          return {
            oijId: data?.oijId ?? oijId,
            totalNotificadas: data?.totalNotificadas ?? 0,
            totalNoNotificadas: data?.totalNoNotificadas ?? 0,
          } as TotalesNotificaciones;
        })
      ) as Observable<TotalesNotificaciones>;
  }

  /**
   * Actualiza los totales de notificaciones para un organo específico, se obtienen los totales utilizando la función correspondiente y se actualiza el subject con los nuevos valores
   * @param oijId 
   * @returns 
   */
  actualizarTotales(oijId: number): Observable<TotalesNotificaciones> {
    return this.obtenerTotales(oijId).pipe(
      tap((totales) => this.totalesSubject.next(totales))
    );
  }

  /**
   * Actualiza los totales de notificaciones para el usuario actual, se obtiene el ID del organo seleccionado y se llama a la función para actualizar los totales
   * @returns 
   */
  actualizarTotalesUsuario(): Observable<TotalesNotificaciones> {
    const oijId = this.getOijIdSeleccionado();
    if (oijId === null || oijId === undefined) {
      const vacio: TotalesNotificaciones = {
        oijId: 0,
        totalNotificadas: 0,
        totalNoNotificadas: 0,
      };
      this.totalesSubject.next(vacio);
      return of(vacio);
    }
    return this.actualizarTotales(oijId);
  }

  /**
   * Obtiene los documentos asociados a una notificación específica, se construye la ruta para la consulta utilizando el ID de la notificación, se hace una petición GET al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con la información de los documentos
   * @param idNotificacionICOIJ 
   * @returns 
   */
  obtenerDocumentosPorNotificacion(
    idNotificacionICOIJ: number
  ): Observable<Documents[]> {
    const url = `oaj.icoij.gateway/icoij/getDocumentosByIdTrans/${idNotificacionICOIJ}/236`;
    return this.httpService.getRequest(url).pipe(
      map((resp: any) => {
        if (resp?.succcess) {
          if (Array.isArray(resp.data)) {
            return resp.data as Documents[];
          }
          if (resp.data?.$values && Array.isArray(resp.data.$values)) {
            return resp.data.$values as Documents[];
          }
        }
        return [] as Documents[];
      })
    ) as Observable<Documents[]>;
  }



  /**
   * Construye una cadena de consulta a partir de los parámetros proporcionados, se convierte el objeto HttpParams en una cadena de consulta y se retorna la cadena resultante con un prefijo '?' si hay parámetros o una cadena vacía si no hay parámetros
   * @param params 
   * @returns 
   */
  private buildQueryString(params: HttpParams): string {
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  /**
   * Obtiene el ID del organo seleccionado por el usuario actual, se recupera el valor encriptado de la sesión y se desencripta para obtener la información del usuario, se extrae el ID del organo y se retorna
   * @returns 
   */
  getOijIdSeleccionado(): number | null {

    const encryptedValue = sessionStorage.getItem(this.currentUserStorageKey);
    const currentUserStr = this.serviceED.Decryption(encryptedValue);
    if (!currentUserStr) {
      return null;
    }

    try {
      const currentUser = JSON.parse(currentUserStr);
      const oijId = Number(
        currentUser?.court ??
        currentUser?.oijId ??
        currentUser?.organoSeleccionadoId
      );
      return Number.isFinite(oijId) ? oijId : null;
    } catch (error) {
      console.error('No fue posible obtener el OIJ seleccionado de sesion.', error);
      return null;
    }
  }

  /**
   * Obtiene el documento de acuerdo asociado a una notificación específica, se construye la ruta para la consulta utilizando los IDs de la notificación y el documento, se hace una petición GET al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con la información del documento
   * @param neun 
   * @param notElec 
   * @returns 
   */
  obtenerDocumentoAcuerdo(
    neun: number, notElec: number
  ): Observable<Documents[]> {
    const url = `oaj.icoij.gateway/icoij/getDoctoAcuerdo/${neun}/${notElec}`;
    return this.httpService.getRequest(url).pipe(
      map((resp: any) => {
        if (resp?.succcess) {
          let acuerdo: Documents = new Documents();
          acuerdo = resp.data;
          return [acuerdo] as Documents[];
        }
        return [] as Documents[];
      })
    ) as Observable<Documents[]>;
  }

  /**
   * Obtiene el documento de acuerdo laboral asociado a una notificación específica, se construye la ruta para la consulta utilizando los IDs de la notificación y el documento, se hace una petición GET al servicio utilizando la ruta construida y se retorna la respuesta como un Observable con la información del documento
   * @param neun 
   * @param notElec 
   * @returns 
   */
  obtenerDocumentoAcuerdoLab(
    neun: number, notElec: number
  ): Observable<Documents[]> {
    const url = `oaj.icoij.gateway/icoij/getDoctoAcuerdoLab/${neun}/${notElec}`;
    return this.httpService.getRequest(url).pipe(
      map((resp: any) => {
        if (resp?.succcess) {
          let acuerdo: Documents = new Documents();
          acuerdo = resp.data;
          return [acuerdo] as Documents[];
        }
        return [] as Documents[];
      })
    ) as Observable<Documents[]>;
  }

}
